import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "image", "title", "counter", "prevBtn", "nextBtn"]
  static values = { 
    photos: String,
    currentIndex: Number 
  }

  connect() {
    if (!this.hasModalTarget) {
      this.manualModal = this.element.querySelector('[data-photo-gallery-target="modal"]')
      console.log('Manual modal search:', this.manualModal)
    }
    
    this.photoData = []
    this.currentIndexValue = 0
    this.bindKeyboardEvents()
    this.bindTouchEvents()
    this.loadPhotos()
  }

  async loadPhotos() {
    try {
      if (!this.photosValue) {
        this.photoData = []
        return
      }

      const response = await fetch(this.photosValue)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      this.photoData = data.photos || []
    } catch (error) {
      console.error('Error loading photos:', error)
      this.photoData = []
    }
  }

  disconnect() {
    this.unbindKeyboardEvents()
    this.unbindTouchEvents()
    document.body.classList.remove('overflow-hidden')
  }

  // Open gallery at specific photo index
  async open(event) {
    event.preventDefault()
    
    // Ensure photos are loaded
    if (!this.photoData || this.photoData.length === 0) {
      await this.loadPhotos()
    }
    
    // Check if photos loaded successfully
    if (!this.photoData || this.photoData.length === 0) {
      console.error('No photos available to display')
      return
    }
    
    const photoIndex = parseInt(event.currentTarget.dataset.photoIndex)
    this.currentIndexValue = photoIndex
    
    // Check if we can find required elements
    const modal = this.modalTarget || this.manualModal || this.element.querySelector('[data-photo-gallery-target="modal"]')
    const image = this.imageTarget || this.element.querySelector('[data-photo-gallery-target="image"]')
    
    if (!modal || !image) {
      console.error('Required elements not found:', {
        modal: !!modal,
        image: !!image
      })
      return
    }
    
    this.showModal()
    this.updateDisplay()
  }

  // Close gallery
  close(event) {
    if (event) {
      event.preventDefault()
    }
    this.hideModal()
  }

  // Navigate to previous photo
  prev(event) {
    event.preventDefault()
    if (this.currentIndexValue > 0) {
      this.currentIndexValue--
      this.updateDisplay()
    }
  }

  // Navigate to next photo
  next(event) {
    event.preventDefault()
    if (this.currentIndexValue < this.photoData.length - 1) {
      this.currentIndexValue++
      this.updateDisplay()
    }
  }

  // Handle backdrop click to close
  backdropClick(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  // Keyboard navigation
  handleKeydown = (event) => {
    const modal = this.modalTarget || this.manualModal || this.element.querySelector('[data-photo-gallery-target="modal"]')
    if (modal && !modal.classList.contains('hidden')) {
      switch(event.key) {
        case 'Escape':
          this.close()
          break
        case 'ArrowLeft':
          this.prev(event)
          break
        case 'ArrowRight':
          this.next(event)
          break
      }
    }
  }

  bindKeyboardEvents() {
    document.addEventListener('keydown', this.handleKeydown)
  }

  unbindKeyboardEvents() {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  // Touch event handling
  bindTouchEvents() {
    this.touchStartX = 0
    this.touchStartY = 0
    this.touchEndX = 0
    this.touchEndY = 0
    
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)
  }

  unbindTouchEvents() {
    const modal = this.modalTarget || this.manualModal || this.element.querySelector('[data-photo-gallery-target="modal"]')
    if (modal) {
      modal.removeEventListener('touchstart', this.handleTouchStart)
      modal.removeEventListener('touchmove', this.handleTouchMove)
      modal.removeEventListener('touchend', this.handleTouchEnd)
    }
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX
    this.touchStartY = event.touches[0].clientY
  }

  handleTouchMove(event) {
    if (!event.touches[0]) return
    
    this.touchEndX = event.touches[0].clientX
    this.touchEndY = event.touches[0].clientY
    
    // Prevent default scrolling during horizontal swipe
    const deltaX = Math.abs(this.touchEndX - this.touchStartX)
    const deltaY = Math.abs(this.touchEndY - this.touchStartY)
    
    if (deltaX > deltaY && deltaX > 10) {
      event.preventDefault()
    }
  }

  handleTouchEnd(event) {
    if (!this.touchStartX || !this.touchEndX) return
    
    const deltaX = this.touchEndX - this.touchStartX
    const deltaY = this.touchEndY - this.touchStartY
    
    // Only trigger if horizontal swipe is greater than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        // Swipe right - go to previous
        this.prev(event)
      } else {
        // Swipe left - go to next
        this.next(event)
      }
    }
    
    // Reset values
    this.touchStartX = 0
    this.touchStartY = 0
    this.touchEndX = 0
    this.touchEndY = 0
  }

  showModal() {
    const modal = this.modalTarget || this.manualModal || this.element.querySelector('[data-photo-gallery-target="modal"]')
    if (!modal) {
      console.error('Modal not found')
      return
    }
    
    modal.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
    
    // Bind touch events to modal
    modal.addEventListener('touchstart', this.handleTouchStart, { passive: false })
    modal.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    modal.addEventListener('touchend', this.handleTouchEnd, { passive: false })
  }

  hideModal() {
    const modal = this.modalTarget || this.manualModal || this.element.querySelector('[data-photo-gallery-target="modal"]')
    if (!modal) {
      console.error('Modal not found')
      return
    }
    
    modal.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    
    // Unbind touch events from modal
    modal.removeEventListener('touchstart', this.handleTouchStart)
    modal.removeEventListener('touchmove', this.handleTouchMove)
    modal.removeEventListener('touchend', this.handleTouchEnd)
  }

  updateDisplay() {
    if (!this.photoData || this.photoData.length === 0) {
      console.error('No photo data available')
      return
    }
    
    const imageEl = this.imageTarget || this.element.querySelector('[data-photo-gallery-target="image"]')
    if (!imageEl) {
      console.error('Image element not found')
      return
    }
    
    const currentPhoto = this.photoData[this.currentIndexValue]
    
    // Update image
    imageEl.src = currentPhoto.large_url
    imageEl.alt = currentPhoto.alt_text || `Photo ${this.currentIndexValue + 1}`
    
    // Update title if available
    const titleEl = this.titleTarget || this.element.querySelector('[data-photo-gallery-target="title"]')
    if (titleEl) {
      titleEl.textContent = currentPhoto.alt_text || ''
      titleEl.style.display = currentPhoto.alt_text ? 'block' : 'none'
    }
    
    // Update counter
    const counterEl = this.counterTarget || this.element.querySelector('[data-photo-gallery-target="counter"]')
    if (counterEl) {
      counterEl.textContent = `${this.currentIndexValue + 1} of ${this.photoData.length}`
    }
    
    // Update navigation buttons
    const prevBtn = this.prevBtnTarget || this.element.querySelector('[data-photo-gallery-target="prevBtn"]')
    if (prevBtn) {
      prevBtn.disabled = this.currentIndexValue === 0
      prevBtn.style.opacity = this.currentIndexValue === 0 ? '0.5' : '1'
    }
    
    const nextBtn = this.nextBtnTarget || this.element.querySelector('[data-photo-gallery-target="nextBtn"]')
    if (nextBtn) {
      nextBtn.disabled = this.currentIndexValue === this.photoData.length - 1
      nextBtn.style.opacity = this.currentIndexValue === this.photoData.length - 1 ? '0.5' : '1'
    }
  }
}